import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles, Button, Grid,
    Switch,
} from '@material-ui/core';
import { InventoryCategory, CustomStockNumberSection } from 'utils/enum/InventoryEnum';
import { FetchPolicy, ALL_LOTS, DataSort } from 'utils/enum/Core';
import { useQuery, useMutation } from '@apollo/client';
import { Form } from 'react-bootstrap';
import UserContext from 'components/context/UserContext';
import LotsCategory, { LotDefaultskey } from 'utils/enum/LotsCategory';
import ButtonStyles from 'styles/theme/Button';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import { VendorType } from 'utils/enum/Vendors';
import InputNumber from 'components/widgets/InputNumber';
import LotQuery from 'services/graphQL/query/LotQuery';
import VendorQuery from 'services/graphQL/query/VendorQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import Select from 'components/widgets/Select';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        paddingBottom: '10px',
        border: `solid 1px ${theme.palette.border.mercury}`,
    },
    noBorder: {
        border: 'initial !important',
    },
    title: {
        fontSize: '12px',
        marginLeft: '15px',
    },
    toggle: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '15px',
        paddingRight: '15px',
        fontSize: '12px',
        marginBottom: '15px',
        '& div.select-bootstrap': {
            width: '180px',
            marginTop: '10px',
        },
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        paddingLeft: '15px',
        paddingRight: '15px',
        marginTop: '20px',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            '& > div:nth-child(2)': {
                display: 'flex',
                justifyContent: 'flex-end',
            },
        },
        '& > div:nth-child(1)': {
            display: 'flex',
            marginRight: '10px',
            borderRight: `1px solid ${theme.palette.border.ghost}`,
            paddingRight: '10px',
            [theme.breakpoints.down('sm')]: {
                borderRight: 'initial',
                marginBottom: '10px',
                justifyContent: 'flex-end',
                paddingRight: 0,
                marginRight: 0,
                '& > button': {
                    width: '80px',
                },
            },
            '& > input': {
                width: '150px',
                marginRight: '10px',
            },
        },
    },
    noBottomMargin: {
        marginBottom: 'auto',
    },
    rightBorder: {
        borderRight: `1px solid ${theme.palette.border.ghost}`,
    },
    prefixesWrapper: {
        display: 'flex',
        alignItems: 'center',
        '& > div': {
            width: '250px !important',
            marginRight: '10px',
            marginTop: '0px !important',
            '& > div': {
                height: '37px !important',
            },
        },
        '& > input': {
            width: '90px',
            marginRight: '10px',
            fontSize: '14px',
        },
        '& > button': {
            height: '34px',
        },
    },
    row: {
        display: 'flex',
    },
}));

const GeneralSettings = ({ canWrite }) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const { refetchUserInformation } = useContext(UserContext);
    const [state, setState] = useState({
        stockNumber: null,
        isRepairsPromptOpen: false,
        enableRepairs: false,
        enableDefaultFloorPlanned: true,
        enableCustomStock: false,
        defaultFlooringCompany: null,
        flooringCompanies: [],
        customStockSection: null,
        customStockSectionsData: {},
        prefix: null,
        suffix: null,
        automaticallyAdvertiseTradeIn: false,
        automaticallyAdvertiseOnRestock: false,
        initialStockNumber: 0,
    });

    const {
        stockNumber,
        isRepairsPromptOpen,
        enableRepairs,
        enableCustomStock,
        customStockSection,
        customStockSectionsData,
        prefix,
        suffix,
        automaticallyAdvertiseTradeIn,
        automaticallyAdvertiseOnRestock,
        initialStockNumber,
    } = state;

    const {
        REPAIRS_MANAGEMENT_ENABLE,
        FLOOR_PLANNED_DEFAULT,
        FLOORING_COMPANY_DEFAULT,
        CUSTOM_STOCK_NUMBER_ENABLE,
        INVENTORY_CUSTOM_STOCK_PREFIX,
        INVENTORY_CUSTOM_STOCK_SUFFIX,
        TRADEIN_CUSTOM_STOCK_PREFIX,
        TRADEIN_CUSTOM_STOCK_SUFFIX,
        REPOSSESSION_CUSTOM_STOCK_PREFIX,
        REPOSSESSION_CUSTOM_STOCK_SUFFIX,
        INITIAL_STOCK_NUMBER,
    } = InventoryCategory;

    const {
        AUTOMATICALLY_ADVERTISE_TRADE_IN,
        AUTOMATICALLY_ADVERTISE_ON_RESTOCK,
    } = LotDefaultskey;

    const {
        data: settingsData,
        loading: settingsLoading,
        error: settingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.INVENTORY,
            lotName: ALL_LOTS,
            key: [
                REPAIRS_MANAGEMENT_ENABLE,
                FLOOR_PLANNED_DEFAULT,
                FLOORING_COMPANY_DEFAULT,
                CUSTOM_STOCK_NUMBER_ENABLE,
                INVENTORY_CUSTOM_STOCK_PREFIX,
                INVENTORY_CUSTOM_STOCK_SUFFIX,
                TRADEIN_CUSTOM_STOCK_PREFIX,
                TRADEIN_CUSTOM_STOCK_SUFFIX,
                REPOSSESSION_CUSTOM_STOCK_PREFIX,
                REPOSSESSION_CUSTOM_STOCK_SUFFIX,
                AUTOMATICALLY_ADVERTISE_TRADE_IN,
                AUTOMATICALLY_ADVERTISE_ON_RESTOCK,
                INITIAL_STOCK_NUMBER,
            ],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: vendorListResponse,
        loading: loadingVendorList,
        error: vendorListError,
    } = useQuery(VendorQuery.GET_VENDOR_LIST, {
        variables: {
            filter: {
                vendorType: [VendorType.FLOORPLAN],
            },
            sort: {
                field: 'vendorName',
                dir: DataSort.ASC,
            },
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [unblockRecords] = useMutation(InventoryMutation.UNBLOCK_VEHICLE_RECORDS, {
        onCompleted: (response) => {
            if (response) {
                ModalUtils.successMessage(null, 'Record(s) was/were unblocked successfully');
                return;
            }

            ModalUtils.errorMessage(null, 'Error unblocking record(s)');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const [saveInventorySettings, { loading: savingSettings }] = useMutation(InventoryMutation.SAVE_INVENTORY_SETTINGS, {
        onCompleted: (response) => {
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    isRepairsPromptOpen: false,
                }));

                if (!response.saveInventorySettings) {
                    ModalUtils.errorMessage(null, 'There was an error trying to save');
                    return;
                }

                refetchUserInformation();
                ModalUtils.successMessage(null, 'Option saved successfully');
                return;
            }

            ModalUtils.errorMessage(null, 'There was an error trying to save');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    useEffect(() => {
        if (settingsError) {
            ModalUtils.errorMessage(settingsError?.graphQLErrors);
            return;
        }

        if (!settingsLoading) {
            const settings = settingsData?.getSettings;
            if (settings) {
                const savedEnableRepairs = settings.find((setting) => setting.key === REPAIRS_MANAGEMENT_ENABLE)?.value ?? 'false';
                const savedFloorPlanned = settings.find((setting) => setting.key === FLOOR_PLANNED_DEFAULT)?.value ?? 'true';
                const savedFlooringCompany = Number(settings.find((setting) => setting.key === FLOORING_COMPANY_DEFAULT)?.value ?? 0);
                const savedEnableCustomStock = settings.find((setting) => setting.key === CUSTOM_STOCK_NUMBER_ENABLE)?.value ?? 'false';
                const savedAutomaticallyAdvertiseTradeIn = settings.find(
                    (setting) => setting.key.toLowerCase() === AUTOMATICALLY_ADVERTISE_TRADE_IN.toLowerCase(),
                )?.value?.toLowerCase() === 'true';

                const savedAutomaticallyAdvertiseOnRestock = settings.find(
                    (setting) => setting.key.toLowerCase() === AUTOMATICALLY_ADVERTISE_ON_RESTOCK.toLowerCase(),
                )?.value?.toLowerCase() === 'true';

                const sectionsPrefixSuffix = [INVENTORY_CUSTOM_STOCK_PREFIX,
                    INVENTORY_CUSTOM_STOCK_SUFFIX,
                    TRADEIN_CUSTOM_STOCK_PREFIX,
                    TRADEIN_CUSTOM_STOCK_SUFFIX,
                    REPOSSESSION_CUSTOM_STOCK_PREFIX,
                    REPOSSESSION_CUSTOM_STOCK_SUFFIX];

                setState({
                    ...state,
                    enableRepairs: savedEnableRepairs === 'true',
                    enableDefaultFloorPlanned: savedFloorPlanned === 'true',
                    defaultFlooringCompany: savedFlooringCompany,
                    enableCustomStock: savedEnableCustomStock === 'true',
                    automaticallyAdvertiseTradeIn: savedAutomaticallyAdvertiseTradeIn,
                    automaticallyAdvertiseOnRestock: savedAutomaticallyAdvertiseOnRestock,
                    customStockSectionsData: sectionsPrefixSuffix.reduce((a, b) => {
                        const isPrefix = b.includes('Prefix');
                        const val = settings.find((setting) => setting.key === b)?.value;
                        if (!val) return a;

                        const newValue = a;
                        if (b.startsWith(CustomStockNumberSection.INVENTORY)) {
                            newValue[CustomStockNumberSection.INVENTORY] = {
                                ...(newValue[CustomStockNumberSection.INVENTORY] ?? {}),
                                ...(isPrefix ? { prefix: val } : { suffix: val }),
                            };
                        }

                        if (b.startsWith(CustomStockNumberSection.TRADE_IN)) {
                            newValue[CustomStockNumberSection.TRADE_IN] = {
                                ...(newValue[CustomStockNumberSection.TRADE_IN] ?? {}),
                                ...(isPrefix ? { prefix: val } : { suffix: val }),
                            };
                        }

                        if (b.startsWith(CustomStockNumberSection.REPOSSESSION)) {
                            newValue[CustomStockNumberSection.REPOSSESSION] = {
                                ...(newValue[CustomStockNumberSection.REPOSSESSION] ?? {}),
                                ...(isPrefix ? { prefix: val } : { suffix: val }),
                            };
                        }

                        return newValue;
                    }, {}),
                    initialStockNumber: settings.find((setting) => setting.key === INITIAL_STOCK_NUMBER)?.value ?? 0,
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settingsError]);

    useEffect(() => {
        if (vendorListError) {
            ModalUtils.errorMessage(vendorListError?.graphQLErrors);
            return;
        }

        if (!loadingVendorList) {
            const vendors = vendorListResponse?.getVendorList;
            if (vendors) {
                setState({
                    ...state,
                    flooringCompanies: vendors,
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingVendorList, vendorListError]);

    const setStockNumber = (value) => {
        setState((prevState) => ({
            ...prevState,
            stockNumber: value,
        }));
    };

    const unblockVehicleRecord = (removeAll = false) => {
        if (!removeAll && (!stockNumber || stockNumber <= 0)) {
            ModalUtils.errorMessage(null, 'Please specify the stock number to unblock');
            return;
        }

        if (!removeAll) {
            unblockRecords({
                variables: {
                    stockNumber,
                },
            });
            return;
        }

        unblockRecords({
            variables: {
                stockNumber: null,
            },
        });
    };

    const toggleRepairsPrompt = () => {
        setState((prevState) => ({
            ...prevState,
            isRepairsPromptOpen: !prevState.isRepairsPromptOpen,
        }));
    };

    const toggleCustomStock = () => {
        setState((prevState) => {
            const newValue = !prevState.enableCustomStock;

            saveInventorySettings({
                variables: {
                    input: [
                        {
                            value: newValue ? 'true' : 'false',
                            key: CUSTOM_STOCK_NUMBER_ENABLE,
                            lotName: ALL_LOTS,
                        },
                    ],
                },
            });

            return {
                ...prevState,
                enableCustomStock: !prevState.enableCustomStock,
            };
        });
    };

    const toggleRepairsSwitch = (accepted = false) => {
        if (!enableRepairs && !accepted) {
            toggleRepairsPrompt();
            return;
        }

        saveInventorySettings({
            variables: {
                input: [
                    {
                        value: accepted ? 'true' : 'false',
                        key: REPAIRS_MANAGEMENT_ENABLE,
                        lotName: ALL_LOTS,
                    },
                ],
            },
        });

        setState((prevState) => ({
            ...prevState,
            enableRepairs: !prevState.enableRepairs,
        }));
    };

    const toggleFloorPlannedSwitch = () => {
        setState((prevState) => {
            const newValue = !prevState.enableDefaultFloorPlanned;

            saveInventorySettings({
                variables: {
                    input: [
                        {
                            value: newValue ? 'true' : 'false',
                            key: FLOOR_PLANNED_DEFAULT,
                            lotName: ALL_LOTS,
                        },
                    ],
                },
            });

            return {
                ...prevState,
                enableDefaultFloorPlanned: newValue,
            };
        });
    };

    const setFlooringCompany = (value) => {
        setState((prevState) => {
            saveInventorySettings({
                variables: {
                    input: [
                        {
                            value: String(value),
                            key: FLOORING_COMPANY_DEFAULT,
                            lotName: ALL_LOTS,
                        },
                    ],
                },
            });

            return {
                ...prevState,
                defaultFlooringCompany: value,
            };
        });
    };

    const changePrefixSuffix = (name, value) => {
        const isCustomStockSection = name === 'customStockSection';
        if (isCustomStockSection) {
            const data = customStockSectionsData[value];

            setState((prevState) => ({
                ...prevState,
                customStockSection: value,
                prefix: (data ?? {}).prefix,
                suffix: (data ?? {}).suffix,
            }));

            return;
        }

        const clone = { ...customStockSectionsData };
        const currentSection = clone[customStockSection];
        if (currentSection) currentSection[name] = value;
        if (!currentSection) clone[customStockSection] = { [name]: value };

        setState((prevState) => ({
            ...prevState,
            [name]: value?.toUpperCase(),
            customStockSectionsData: clone,
        }));
    };

    const saveCustomStockData = () => {
        const input = [];
        const keys = Object.keys(customStockSectionsData);

        keys.forEach((k) => {
            const { prefix: prefixVal, suffix: suffixVal } = customStockSectionsData[k];

            input.push(
                {
                    value: !StringUtils.isEmpty(prefixVal) ? prefixVal.toUpperCase() : '',
                    key: `${k}CustomStockPrefix`,
                    lotName: ALL_LOTS,
                },
            );

            input.push(
                {
                    value: !StringUtils.isEmpty(suffixVal) ? suffixVal.toUpperCase() : '',
                    key: `${k}CustomStockSuffix`,
                    lotName: ALL_LOTS,
                },
            );
        });

        if (input.length > 0) {
            saveInventorySettings({
                variables: {
                    input,
                },
            });
        }
    };

    const toggleAutomaticallyAdvertiseTradeIn = () => {
        setState((prevState) => {
            const newValue = !prevState.automaticallyAdvertiseTradeIn;

            saveInventorySettings({
                variables: {
                    input: [{
                        lotName: ALL_LOTS,
                        value: newValue ? 'true' : 'false',
                        key: AUTOMATICALLY_ADVERTISE_TRADE_IN,
                    }],
                },
            });

            return {
                ...prevState,
                automaticallyAdvertiseTradeIn: newValue,
            };
        });
    };

    const toggleAutomaticallyAdvertiseOnRestock = () => {
        setState((prevState) => {
            const newValue = !prevState.automaticallyAdvertiseOnRestock;

            saveInventorySettings({
                variables: {
                    input: [{
                        lotName: ALL_LOTS,
                        value: newValue ? 'true' : 'false',
                        key: AUTOMATICALLY_ADVERTISE_ON_RESTOCK,
                    }],
                },
            });

            return {
                ...prevState,
                automaticallyAdvertiseOnRestock: newValue,
            };
        });
    };

    const setInitialStockNumber = (value) => {
        setState((prevState) => ({
            ...prevState,
            initialStockNumber: value,
        }));
    };

    return (
        <>
            <Grid container className={classes.box}>
                <Grid item xs={12}>
                    <div className={classes.title}>
                        Remove blocking the inventory from editing due to it is being edited by another user
                    </div>
                </Grid>
                <Grid className={classes.actions} item xs={12}>
                    <div>
                        <InputNumber
                            size="sm"
                            value={stockNumber}
                            removeDecimalScale
                            placeholder="Stock Number"
                            onChange={(value) => setStockNumber(value)}
                        />
                        <Button
                            disabled={!canWrite}
                            className={classes.containedSecondaryInfo}
                            size="small"
                            onClick={() => unblockVehicleRecord(false)}
                        >
                            Remove
                        </Button>
                    </div>
                    <div>
                        <Button
                            disabled={!canWrite}
                            className={classes.containedSecondaryInfo}
                            size="small"
                            onClick={() => unblockVehicleRecord(true)}
                        >
                            Remove All
                        </Button>
                    </div>
                </Grid>
            </Grid>
            <Grid container className={classes.box}>
                <Grid className={clsx(classes.toggle, classes.rightBorder, classes.noBottomMargin)} item md={2} xs={12}>
                    <div>
                        Use Custom Stock Number
                    </div>
                    <div>
                        <Switch
                            disabled={savingSettings}
                            checked={enableCustomStock}
                            onChange={() => toggleCustomStock()}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                </Grid>
                <Grid className={clsx(classes.toggle, classes.noBottomMargin)} item md={10} xs={12}>
                    <div>
                        Default Prefixes and Suffixes
                    </div>
                    <div className={classes.prefixesWrapper}>
                        <Select
                            nowrap
                            size="sm"
                            disabled={!enableCustomStock}
                            loading={false}
                            className={classes.input}
                            name="customStockSection"
                            onChange={(name, value) => changePrefixSuffix(name, value)}
                            value={customStockSection ?? ''}
                            options={Object.values(CustomStockNumberSection).map((section) => ({
                                value: section,
                                label: section === CustomStockNumberSection.TRADE_IN ? 'Trade-in' : section,
                            }))}
                        />
                        <Form.Control
                            className={classes.input}
                            disabled={!enableCustomStock || !customStockSection}
                            placeholder="Prefix"
                            type="text"
                            name="prefix"
                            value={prefix ?? ''}
                            onChange={({ target: { name, value } }) => changePrefixSuffix(name, value)}
                        />
                        <Form.Control
                            className={classes.input}
                            disabled={!enableCustomStock || !customStockSection}
                            placeholder="Suffix"
                            type="text"
                            name="suffix"
                            value={suffix ?? ''}
                            onChange={({ target: { name, value } }) => changePrefixSuffix(name, value)}
                        />
                        <Button
                            disabled={!enableCustomStock || !customStockSection || Object.keys(customStockSectionsData).length === 0 || savingSettings}
                            className={classes.containedSecondaryInfo}
                            size="small"
                            onClick={() => saveCustomStockData()}
                        >
                            Save
                        </Button>
                    </div>
                </Grid>
            </Grid>
            <Grid container className={clsx(classes.box, classes.noBorder)}>
                <Grid item xs={12} className={classes.row}>
                    <Grid className={classes.toggle}>
                        <div>
                            Enable ability to add repairs to inventory
                        </div>
                        <div>
                            <Switch
                                disabled={savingSettings}
                                checked={enableRepairs}
                                onChange={() => toggleRepairsSwitch()}
                                inputProps={{ 'aria-label': 'primary checkbox' }}
                            />
                        </div>
                    </Grid>
                    <Grid className={classes.toggle}>
                        <div>
                            Automatically advertise trade in&apos;s
                        </div>
                        <div>
                            <Switch
                                disabled={savingSettings}
                                checked={automaticallyAdvertiseTradeIn}
                                onChange={toggleAutomaticallyAdvertiseTradeIn}
                                inputProps={{ 'aria-label': 'primary checkbox' }}
                            />
                        </div>
                    </Grid>
                    <Grid className={classes.toggle}>
                        <div>
                            Automatically advertise repossessed vehicles on restock
                        </div>
                        <div>
                            <Switch
                                disabled={savingSettings}
                                checked={automaticallyAdvertiseOnRestock}
                                onChange={toggleAutomaticallyAdvertiseOnRestock}
                                inputProps={{ 'aria-label': 'primary checkbox' }}
                            />
                        </div>
                    </Grid>
                </Grid>
                <Grid className={classes.toggle} item xs={12}>
                    <div>
                        Floor Planned by default
                    </div>
                    <div>
                        <Switch
                            disabled={savingSettings}
                            checked={state.enableDefaultFloorPlanned}
                            onChange={() => toggleFloorPlannedSwitch()}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                </Grid>
                <Grid className={classes.toggle} item xs={12}>
                    <div>
                        Flooring Company by default
                    </div>
                    <div>
                        <Select
                            nowrap
                            size="sm"
                            loading={loadingVendorList}
                            disabled={savingSettings}
                            className={classes.input}
                            name="flooringCompany"
                            onChange={(_, value) => setFlooringCompany(value)}
                            value={state.defaultFlooringCompany ?? 0}
                            options={state.flooringCompanies.map((item) => ({
                                value: item.vendorId,
                                label: item.vendorName,
                            }))}
                        />
                    </div>
                </Grid>
                <Grid className={classes.toggle} item xs={12} sm={3}>
                    <div>
                        Initial Stock Number
                    </div>
                    <div style={{ display: 'flex', gap: 5 }}>
                        <InputNumber
                            size="sm"
                            value={initialStockNumber}
                            removeDecimalScale
                            placeholder="Initial Stock Number"
                            onChange={(value) => setInitialStockNumber(value)}
                        />
                        <Button
                            className={classes.containedSecondaryInfo}
                            size="small"
                            onClick={() => saveInventorySettings({
                                variables: {
                                    input: [{
                                        lotName: ALL_LOTS,
                                        value: String(initialStockNumber),
                                        key: INITIAL_STOCK_NUMBER,
                                    }],
                                },
                            })}
                        >
                            Save
                        </Button>
                    </div>
                </Grid>
            </Grid>
            <ConfirmDialog
                title="Warning"
                description="Activating this feature may cause discrepancy between Accounting and Inventory. Are you sure you want to proceed?"
                open={isRepairsPromptOpen}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="No"
                onClose={toggleRepairsPrompt}
                onClickSecondary={toggleRepairsPrompt}
                onClickPrimary={() => toggleRepairsSwitch(true)}
                disablePrimaryButton={settingsLoading || savingSettings}
            />
        </>
    );
};

GeneralSettings.propTypes = {
    canWrite: PropTypes.bool.isRequired,
};

export default GeneralSettings;
